import React from "react";
import { Link } from "react-router-dom";

// ============================================================
// PAGE LAYOUT
// ============================================================

export function PageContainer({ children, wide }: { children: React.ReactNode; wide?: boolean }) {
  return (
    <div className={`${wide ? "max-w-full" : "max-w-7xl"} mx-auto px-8 py-8`}>
      {children}
    </div>
  );
}

export function PageHeader({
  title,
  subtitle,
  action,
  backTo,
}: {
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
  backTo?: string;
}) {
  return (
    <div className="mb-8">
      {backTo && (
        <Link
          to={backTo}
          className="inline-flex items-center gap-1.5 text-sm font-medium mb-3 transition-colors"
          style={{ color: "var(--rbw-gray-dark)" }}
          onMouseEnter={(e) => (e.currentTarget.style.color = "var(--rbw-navy)")}
          onMouseLeave={(e) => (e.currentTarget.style.color = "var(--rbw-gray-dark)")}
        >
          &larr; Back
        </Link>
      )}
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ color: "var(--rbw-navy)" }}>
            {title}
          </h1>
          {subtitle && (
            <p className="mt-1 text-sm" style={{ color: "var(--rbw-gray-dark)" }}>
              {subtitle}
            </p>
          )}
        </div>
        {action && <div>{action}</div>}
      </div>
    </div>
  );
}

// ============================================================
// CARDS
// ============================================================

export function Card({
  children,
  className = "",
  padding = "p-6",
}: {
  children: React.ReactNode;
  className?: string;
  padding?: string;
}) {
  return (
    <div
      className={`rounded-2xl ${padding} ${className}`}
      style={{
        background: "var(--rbw-bg-card)",
        border: "1px solid var(--rbw-border)",
        boxShadow: "0 1px 3px rgba(51,63,72,0.04), 0 4px 12px rgba(51,63,72,0.03)",
      }}
    >
      {children}
    </div>
  );
}

export function CardHeader({
  title,
  action,
}: {
  title: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between mb-5 pb-4" style={{ borderBottom: "1px solid var(--rbw-border)" }}>
      <h2 className="text-lg font-bold" style={{ color: "var(--rbw-navy)" }}>
        {title}
      </h2>
      {action}
    </div>
  );
}

export function StatCard({
  label,
  value,
  detail,
  variant = "default",
}: {
  label: string;
  value: string;
  detail?: string;
  variant?: "default" | "gold" | "blue" | "green" | "danger";
}) {
  const variants = {
    default: { bg: "var(--rbw-bg)", border: "var(--rbw-border)", label: "var(--rbw-gray-dark)", value: "var(--rbw-navy)" },
    gold: { bg: "#F6BE0010", border: "#F6BE0030", label: "var(--rbw-gold-dark)", value: "var(--rbw-navy)" },
    blue: { bg: "#8BB8E810", border: "#8BB8E830", label: "var(--rbw-blue-dark)", value: "var(--rbw-navy)" },
    green: { bg: "#2D8A4E10", border: "#2D8A4E30", label: "var(--rbw-success)", value: "var(--rbw-navy)" },
    danger: { bg: "#C5303010", border: "#C5303030", label: "var(--rbw-danger)", value: "var(--rbw-navy)" },
  };
  const v = variants[variant];

  return (
    <div className="rounded-xl p-5" style={{ background: v.bg, border: `1px solid ${v.border}` }}>
      <div className="text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: v.label }}>
        {label}
      </div>
      <div className="text-2xl font-bold" style={{ color: v.value }}>
        {value}
      </div>
      {detail && (
        <div className="text-xs mt-1" style={{ color: "var(--rbw-gray-dark)" }}>
          {detail}
        </div>
      )}
    </div>
  );
}

// ============================================================
// TABLES
// ============================================================

export function Table({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="rounded-2xl overflow-hidden"
      style={{
        background: "var(--rbw-bg-card)",
        border: "1px solid var(--rbw-border)",
        boxShadow: "0 1px 3px rgba(51,63,72,0.04), 0 4px 12px rgba(51,63,72,0.03)",
      }}
    >
      <table className="w-full">
        {children}
      </table>
    </div>
  );
}

export function Thead({ children }: { children: React.ReactNode }) {
  return (
    <thead>
      <tr style={{ background: "var(--rbw-navy)" }}>
        {children}
      </tr>
    </thead>
  );
}

export function Th({
  children,
  align = "left",
}: {
  children: React.ReactNode;
  align?: "left" | "center" | "right";
}) {
  return (
    <th
      className={`text-${align} px-5 py-3.5 text-xs font-semibold uppercase tracking-wider`}
      style={{ color: "var(--rbw-gray)" }}
    >
      {children}
    </th>
  );
}

export function Td({
  children,
  align = "left",
  bold = false,
}: {
  children: React.ReactNode;
  align?: "left" | "center" | "right";
  bold?: boolean;
}) {
  return (
    <td
      className={`text-${align} px-5 py-4 text-sm ${bold ? "font-semibold" : ""}`}
      style={{ color: "var(--rbw-navy)" }}
    >
      {children}
    </td>
  );
}

export function TRow({
  children,
  index = 0,
}: {
  children: React.ReactNode;
  index?: number;
}) {
  return (
    <tr style={{
      borderBottom: "1px solid var(--rbw-border)",
      background: index % 2 === 0 ? "transparent" : "var(--rbw-bg)",
    }}>
      {children}
    </tr>
  );
}

export function EmptyState({
  message,
  detail,
  icon,
}: {
  message: string;
  detail?: string;
  icon?: string;
}) {
  return (
    <div className="text-center py-16">
      {icon && <div className="text-5xl mb-4 opacity-30">{icon}</div>}
      <div className="text-lg font-medium" style={{ color: "var(--rbw-gray-dark)" }}>{message}</div>
      {detail && <div className="text-sm mt-1" style={{ color: "var(--rbw-gray)" }}>{detail}</div>}
    </div>
  );
}

// ============================================================
// BUTTONS
// ============================================================

export function Button({
  children,
  onClick,
  variant = "primary",
  size = "md",
  disabled = false,
  type = "button",
  className = "",
}: {
  children: React.ReactNode;
  onClick?: () => void;
  variant?: "primary" | "secondary" | "ghost" | "danger" | "gold";
  size?: "sm" | "md" | "lg";
  disabled?: boolean;
  type?: "button" | "submit";
  className?: string;
}) {
  const variants = {
    primary: { bg: "var(--rbw-gold)", color: "var(--rbw-navy)", hover: "var(--rbw-gold-dark)" },
    secondary: { bg: "var(--rbw-navy)", color: "#ffffff", hover: "var(--rbw-navy-light)" },
    ghost: { bg: "var(--rbw-gray-light)", color: "var(--rbw-navy)", hover: "var(--rbw-gray)" },
    danger: { bg: "var(--rbw-danger)", color: "#ffffff", hover: "#a02020" },
    gold: { bg: "var(--rbw-gold)", color: "var(--rbw-navy)", hover: "var(--rbw-gold-dark)" },
  };
  const sizes = {
    sm: "px-3 py-1.5 text-sm rounded-lg",
    md: "px-5 py-2.5 rounded-xl",
    lg: "px-7 py-3.5 text-lg rounded-xl",
  };

  const v = variants[variant];

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`font-semibold transition-all active:scale-[0.97] disabled:opacity-40 disabled:cursor-not-allowed ${sizes[size]} ${className}`}
      style={{ background: v.bg, color: v.color }}
      onMouseEnter={(e) => { if (!disabled) (e.currentTarget.style.background = v.hover); }}
      onMouseLeave={(e) => { if (!disabled) (e.currentTarget.style.background = v.bg); }}
    >
      {children}
    </button>
  );
}

export function LinkButton({
  to,
  children,
  variant = "primary",
}: {
  to: string;
  children: React.ReactNode;
  variant?: "primary" | "secondary" | "ghost";
}) {
  const variants = {
    primary: { bg: "var(--rbw-gold)", color: "var(--rbw-navy)" },
    secondary: { bg: "var(--rbw-navy)", color: "#ffffff" },
    ghost: { bg: "var(--rbw-gray-light)", color: "var(--rbw-navy)" },
  };
  const v = variants[variant];

  return (
    <Link
      to={to}
      className="px-5 py-2.5 rounded-xl font-semibold transition-all inline-block"
      style={{ background: v.bg, color: v.color }}
    >
      {children}
    </Link>
  );
}

// ============================================================
// FORMS
// ============================================================

export function FormGroup({
  label,
  children,
  span = 1,
}: {
  label: string;
  children: React.ReactNode;
  span?: 1 | 2;
}) {
  return (
    <div className={span === 2 ? "col-span-2" : ""}>
      <label className="block text-sm font-semibold mb-1.5" style={{ color: "var(--rbw-navy)" }}>
        {label}
      </label>
      {children}
    </div>
  );
}

export function Input({
  value,
  onChange,
  placeholder,
  type = "text",
  required = false,
  step,
  min,
  className = "",
}: {
  value: string | number;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: string;
  required?: boolean;
  step?: string;
  min?: string;
  className?: string;
}) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      required={required}
      step={step}
      min={min}
      className={`w-full px-4 py-2.5 rounded-xl outline-none transition-all ${className}`}
      style={{
        border: "2px solid var(--rbw-border)",
        background: "var(--rbw-bg-card)",
        color: "var(--rbw-navy)",
      }}
      onFocus={(e) => (e.target.style.borderColor = "var(--rbw-blue)")}
      onBlur={(e) => (e.target.style.borderColor = "var(--rbw-border)")}
    />
  );
}

export function Select({
  value,
  onChange,
  children,
  className = "",
}: {
  value: string;
  onChange: (value: string) => void;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={`w-full px-4 py-2.5 rounded-xl outline-none transition-all ${className}`}
      style={{
        border: "2px solid var(--rbw-border)",
        background: "var(--rbw-bg-card)",
        color: "var(--rbw-navy)",
      }}
      onFocus={(e) => (e.target.style.borderColor = "var(--rbw-blue)")}
      onBlur={(e) => (e.target.style.borderColor = "var(--rbw-border)")}
    >
      {children}
    </select>
  );
}

// ============================================================
// BADGES
// ============================================================

export function Badge({
  children,
  variant = "default",
}: {
  children: React.ReactNode;
  variant?: "default" | "gold" | "blue" | "green" | "danger" | "purple";
}) {
  const variants = {
    default: { bg: "var(--rbw-gray-light)", color: "var(--rbw-gray-dark)", border: "var(--rbw-gray)" },
    gold: { bg: "#F6BE0015", color: "var(--rbw-gold-dark)", border: "#F6BE0040" },
    blue: { bg: "#8BB8E815", color: "var(--rbw-blue-dark)", border: "#8BB8E840" },
    green: { bg: "#2D8A4E15", color: "var(--rbw-success)", border: "#2D8A4E40" },
    danger: { bg: "#C5303015", color: "var(--rbw-danger)", border: "#C5303040" },
    purple: { bg: "#7C3AED15", color: "#7C3AED", border: "#7C3AED40" },
  };
  const v = variants[variant];

  return (
    <span
      className="inline-flex px-2.5 py-1 rounded-full text-xs font-semibold"
      style={{ background: v.bg, color: v.color, border: `1px solid ${v.border}` }}
    >
      {children}
    </span>
  );
}

export function CategoryBadge({ category }: { category: string }) {
  const colorMap: Record<string, "gold" | "blue" | "green" | "default" | "danger" | "purple"> = {
    domestic: "gold",
    import: "blue",
    craft: "green",
    seltzer: "blue",
    rtd: "danger",
    malternative: "gold",
    cider: "green",
    na: "default",
    accessory: "default",
  };
  return <Badge variant={colorMap[category] || "default"}>{category}</Badge>;
}

// ============================================================
// MISC
// ============================================================

export function Divider() {
  return <div className="my-6" style={{ borderTop: "1px solid var(--rbw-border)" }} />;
}

export function Muted({ children }: { children: React.ReactNode }) {
  return <span style={{ color: "var(--rbw-gray-dark)" }}>{children}</span>;
}

export function AlertBanner({
  children,
  variant = "gold",
}: {
  children: React.ReactNode;
  variant?: "gold" | "blue" | "green" | "danger";
}) {
  const variants = {
    gold: { bg: "#F6BE0010", border: "#F6BE0035", color: "var(--rbw-gold-dark)" },
    blue: { bg: "#8BB8E810", border: "#8BB8E835", color: "var(--rbw-blue-dark)" },
    green: { bg: "#2D8A4E10", border: "#2D8A4E35", color: "var(--rbw-success)" },
    danger: { bg: "#C5303010", border: "#C5303035", color: "var(--rbw-danger)" },
  };
  const v = variants[variant];

  return (
    <div className="rounded-xl p-4 mb-6" style={{ background: v.bg, border: `1px solid ${v.border}`, color: v.color }}>
      {children}
    </div>
  );
}

export function TabGroup({
  tabs,
  active,
  onChange,
}: {
  tabs: Array<{ key: string; label: string }>;
  active: string;
  onChange: (key: string) => void;
}) {
  return (
    <div className="flex gap-1 mb-6 p-1 rounded-xl inline-flex" style={{ background: "var(--rbw-gray-light)" }}>
      {tabs.map((tab) => (
        <button
          key={tab.key}
          onClick={() => onChange(tab.key)}
          className="px-4 py-2 rounded-lg text-sm font-medium transition-all"
          style={active === tab.key
            ? { background: "var(--rbw-navy)", color: "#fff", boxShadow: "0 1px 3px rgba(0,0,0,0.2)" }
            : { background: "transparent", color: "var(--rbw-navy)" }
          }
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}
